import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Part, PartDraft } from '@/types/Part';

const COLLECTION = 'parts';

function partsCollection() {
  return collection(db, COLLECTION);
}

function partDoc(id: string) {
  return doc(db, COLLECTION, id);
}

function draftToFirestore(draft: PartDraft, { isUpdate }: { isUpdate: boolean }): Record<string, unknown> {
  const verifiedDate = draft.authentication.verifiedDate
    ? Timestamp.fromDate(new Date(draft.authentication.verifiedDate))
    : null;
  const base: Record<string, unknown> = {
    sku: draft.sku,
    title: draft.title,
    year: draft.year,
    category: draft.category,
    iconId: draft.iconId,
    type: draft.type,
    condition: draft.condition,
    price: draft.price,
    weight: draft.weight,
    casting: draft.casting ?? '',
    dateCode: draft.dateCode ?? '',
    fitment: draft.fitment,
    description: draft.description,
    photos: draft.photos,
    authentication: {
      verifiedBy: draft.authentication.verifiedBy,
      verifiedDate,
      notes: draft.authentication.notes,
    },
    status: draft.status,
    stock: draft.stock,
    updatedAt: serverTimestamp(),
  };
  if (!isUpdate) {
    base['createdAt'] = serverTimestamp();
  }
  return base;
}

export async function fetchAllParts(): Promise<Part[]> {
  const snap = await getDocs(query(partsCollection(), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Part));
}

export async function fetchPart(id: string): Promise<Part | null> {
  const snap = await getDoc(partDoc(id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Part;
}

export async function createPart(draft: PartDraft): Promise<string> {
  await setDoc(partDoc(draft.id), draftToFirestore(draft, { isUpdate: false }));
  return draft.id;
}

export async function updatePart(id: string, draft: PartDraft): Promise<void> {
  await setDoc(partDoc(id), draftToFirestore(draft, { isUpdate: true }), { merge: true });
}

export async function deletePart(id: string): Promise<void> {
  await deleteDoc(partDoc(id));
}

export async function batchCreateParts(drafts: readonly PartDraft[]): Promise<number> {
  // Firestore batch limit is 500; split if larger.
  const chunks: PartDraft[][] = [];
  const CHUNK = 400;
  for (let i = 0; i < drafts.length; i += CHUNK) {
    chunks.push(drafts.slice(i, i + CHUNK));
  }
  let count = 0;
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    for (const draft of chunk) {
      batch.set(partDoc(draft.id), draftToFirestore(draft, { isUpdate: false }));
    }
    await batch.commit();
    count += chunk.length;
  }
  return count;
}
